'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  clearAuthCookies,
  getAuthCookies,
  logout,
  verifySession,
} from './actions';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContextType, SessionData, User } from './types';
import { clientLogout } from '../api/hooks/useUser';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({
  children,
  config,
}: {
  children: React.ReactNode;
  config?: {
    apiBaseUrl: string;
  };
}) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<SessionData | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const authRoutes = [
    '/auth/login',
    '/auth/signup',
    '/forgot-password',
    '/reset-password',
    '/legal',
  ];
  /* ======================================================
     INITIAL SESSION CHECK (NO REFRESH HERE)
  ====================================================== */
  useEffect(() => {
    const init = async () => {
      try {
        const { accessToken, refreshToken } = await getAuthCookies();

        if (!accessToken || !refreshToken) {
          setLoading(false);
          // ako si na protected ruti, prebaci

          if (!authRoutes.some((r) => pathname.startsWith(r))) {
            router.replace('/auth/login');
          }
          return;
        }
        const sessionData = await verifySession(
          accessToken,
          refreshToken,
          config?.apiBaseUrl + '/auth' || '',
        );
        // const res = await fetch(`${config?.apiBaseUrl}/auth`, {
        //   method: 'GET',
        //   headers: {
        //     Authorization: `Bearer ${accessToken}`,
        //   },
        //   credentials: 'include',
        // });

        if (!sessionData) {
          await forceLogout();
          return;
        }

        // const data = await res.json();

        // setSession(data);
        // setUser(data.user);
        setSession(sessionData);
        setUser(sessionData.user);
      } catch {
        await forceLogout();
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  /* ======================================================
     GLOBAL LOGOUT LISTENER (triggered from apiClient)
  ====================================================== */
  useEffect(() => {
    const handler = async () => {
      await forceLogout();
    };

    document.addEventListener('auth:logout', handler);
    return () => document.removeEventListener('auth:logout', handler);
  }, []);

  /* ======================================================
     FORCE LOGOUT (DETERMINISTIC)
  ====================================================== */
  // const forceLogout = async () => {
  //   setLoggingOut(true);

  //   try {
  //     await logout(); // backend cleanup (optional)
  //   } catch {}

  //   setSession(null);
  //   setUser(null);
  //   if (!pathname.startsWith('/auth')) {
  //     setLoading(false);
  //     window.location.replace('/auth/login');
  //   }
  // };
  const forceLogout = async () => {
    setLoggingOut(true);

    try {
      // pošalji logout prema API-ju
      await clientLogout(session?.accessToken);
    } catch {}

    // očisti local state
    setSession(null);
    setUser(null);

    // očisti access/refresh cookies (Next side)
    await clearAuthCookies();

    if (!pathname.startsWith('/auth')) {
      window.location.replace('/auth/login');
    }
  };

  /* ======================================================
     MANUAL USER REFRESH
  ====================================================== */
  const refreshUser = async () => {
    const { accessToken } = await getAuthCookies();
    if (!accessToken) return;

    const res = await fetch(`${config?.apiBaseUrl}/auth`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });

    if (!res.ok) {
      await forceLogout();
      return;
    }

    const data = await res.json();
    setSession(data);
    setUser(data.user);
  };

  /* ====================================================== */

  if (isLoading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        setSession,
        logoutSession: forceLogout,
        isLoading,
        refreshUser,
      }}>
      {loggingOut ? (
        <div className='flex h-screen items-center justify-center'>
          <Loader2 className='h-8 w-8 animate-spin' />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

/* ======================================================
   HOOK
====================================================== */

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
